import { useState, useEffect, ReactNode } from "react";
import { Box, Chip } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { request } from "../../../config/request.ts";
import DataTable, { Column } from "../../../shared/DataTable/index.tsx";
import PageHeader from "../../../shared/PageHeader/index.tsx";
import PageWrapper from "../../../shared/PageWrapper/index.tsx";
import DateRangeSelector from "../../../shared/DateRangeSelector/index.tsx";
import SelectField from "../../../shared/SelectField/index.tsx";
import { formatDate, formatTime } from "../../../config/formatters.ts";
import dayjs, { Dayjs } from "dayjs";

interface Transaction {
  id: number;
  transactionDate?: string;
  transactionTime?: string;
  transactionType?: string;
  amount?: number;
  remarks?: string;
  ledger?: { id: number; ledgerName: string } | null;
  account?: {
    id: number;
    accountName: string;
    accountNo?: string;
    accountType?: string;
  } | null;
  fromAccount?: { id: number; accountName: string } | null;
  toAccount?: { id: number; accountName: string } | null;
  wash?: {
    id: number;
    vehicle?: { regNo: string; vehicleDetails?: string } | null;
  } | null;
}

const emptyFilters = { transactionType: "", ledgerId: "", accountId: "" };

export default function TransactionReport() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [ledgers, setLedgers] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [totals, setTotals] = useState({
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
  });
  const [dateRange, setDateRange] = useState<{
    from: Dayjs | null;
    to: Dayjs | null;
  }>({
    from: dayjs(),
    to: dayjs(),
  });

  useEffect(() => {
    getTransactions();
  }, [
    page,
    limit,
    filters.transactionType,
    filters.ledgerId,
    filters.accountId,
    dateRange.from,
    dateRange.to,
  ]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filters, dateRange.from, dateRange.to]);

  const filterFormik = {
    values: filters,
    errors: {},
    touched: {},
    handleChange: (e: any) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    handleBlur: () => {},
    setFieldValue: () => {},
  } as any;

  const fetchFilterOptions = async () => {
    try {
      const res = await request.get("/api/getLedgers", {
        limit: 200,
        offset: 0,
        orderColumn: "ledgerName",
        orderDirection: "asc",
      });
      setLedgers(res?.data?.data || []);
    } catch {
      setLedgers([]);
    }
    try {
      const res = await request.get("/api/staff/getActiveAccounts");
      setAccounts(res?.data?.accounts || []);
    } catch {
      setAccounts([]);
    }
  };

  const buildFilterParams = () => {
    const params: Record<string, any> = {};
    if (dateRange.from) params.startDate = dateRange.from.format("YYYY-MM-DD");
    if (dateRange.to) params.endDate = dateRange.to.format("YYYY-MM-DD");
    if (filters.transactionType)
      params.transactionType = filters.transactionType;
    if (filters.ledgerId) params.ledgerId = filters.ledgerId;
    if (filters.accountId) params.accountId = filters.accountId;
    return params;
  };

  const getTransactions = async () => {
    setLoading(true);
    const params: Record<string, any> = {
      limit,
      offset: page * limit,
      ...buildFilterParams(),
    };

    const response = await request.get("/api/getTransactions", params);
    const payload = response?.data;
    setTransactions(
      Array.isArray(payload?.data) ? payload.data : payload?.data?.data || [],
    );
    setTotalCount(payload?.count ?? 0);
    setTotals({
      totalIncome: payload?.totalIncome ?? payload?.data?.totalIncome ?? 0,
      totalExpense: payload?.totalExpense ?? payload?.data?.totalExpense ?? 0,
      balance: payload?.balance ?? payload?.data?.balance ?? 0,
    });
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const handleDateRangeChange = (range: { from: Dayjs | null; to: Dayjs | null }) => {
    setDateRange(range);
  };

  const formatAccount = (row: Transaction) => {
    if (row?.fromAccount && row?.toAccount) {
      return `${row.fromAccount.accountName} → ${row.toAccount.accountName}`;
    }
    const account = row?.account;
    if (!account) return "-";
    return `${account.accountName}${
      account.accountNo ? ` (${account.accountNo})` : ""
    }`;
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await request.download(
        "/api/downloadTransactionReport",
        buildFilterParams(),
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      let filename = `TransactionReport_${(dateRange.from || dayjs()).format(
        "YYYY-MM-DD",
      )}_to_${(dateRange.to || dayjs()).format("YYYY-MM-DD")}.xlsx`;
      const disposition = response.headers?.["content-disposition"] as
        | string
        | undefined;
      const match = disposition?.match(/filename="?([^";]+)"?/);
      if (match?.[1]) filename = match[1];

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setDownloading(false);
    }
  };

  const hasFilters = Boolean(
    filters.transactionType || filters.ledgerId || filters.accountId,
  );

  const formatAmount = (value: number) =>
    value != null ? `₹${Number(value).toLocaleString()}` : "-";

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "transactionDate",
      label: "Date",
      minWidth: 110,
      format: (value) => formatDate(value),
    },
    {
      id: "transactionTime",
      label: "Time",
      minWidth: 90,
      format: (value) => formatTime(value),
    },
    {
      id: "transactionType",
      label: "Type",
      minWidth: 100,
      align: "center",
      format: (value) => (
        <Chip
          label={value || "-"}
          size="small"
          sx={{
            background:
              value === "Income"
                ? "rgba(16, 185, 129, 0.1)"
                : "rgba(239, 68, 68, 0.1)",
            color: value === "Income" ? "#059669" : "#dc2626",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      ),
    },
    {
      id: "ledger",
      label: "Ledger",
      minWidth: 150,
      format: (_value, row) => row?.ledger?.ledgerName || "-",
    },
    {
      id: "account",
      label: "Account",
      minWidth: 170,
      format: (_value, row) => formatAccount(row),
    },
    {
      id: "regNo",
      label: "Vehicle No",
      minWidth: 120,
      format: (_value, row) => row?.wash?.vehicle?.regNo || "-",
    },
    {
      id: "vehicleDetails",
      label: "Vehicle Details",
      minWidth: 200,
      format: (_value, row) => row?.wash?.vehicle?.vehicleDetails || "-",
    },
    {
      id: "amount",
      label: "Amount",
      minWidth: 130,
      align: "right",
      format: (value, row) => {
        const isIncome = row?.transactionType === "Income";
        return (
          <Box
            sx={{
              fontWeight: 700,
              fontSize: "0.8125rem",
              color: isIncome ? "#059669" : "#dc2626",
            }}
          >
            {isIncome ? "+ " : "- "}
            {formatAmount(value)}
          </Box>
        );
      },
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Transaction Report"
        subtitle="Income and expenses of the selected period"
        filters={
          <DateRangeSelector value={dateRange} onChange={handleDateRangeChange} />
        }
        secondaryButton={{
          label: downloading ? "Downloading..." : "Download Excel",
          onClick: handleDownloadExcel,
        }}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <SummaryCard
          label="Total Income"
          value={totals.totalIncome}
          tone="income"
          icon={<ArrowUpwardIcon sx={{ fontSize: 18 }} />}
        />
        <SummaryCard
          label="Total Expense"
          value={totals.totalExpense}
          tone="expense"
          icon={<ArrowDownwardIcon sx={{ fontSize: 18 }} />}
        />
        <SummaryCard
          label="Balance"
          value={totals.balance}
          tone={totals.balance < 0 ? "expense" : "income"}
          icon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 190 }}>
          <SelectField
            formik={filterFormik}
            name="transactionType"
            label="Transaction Type"
            options={[
              { value: "", label: "All" },
              { value: "Income", label: "Income" },
              { value: "Expense", label: "Expense" },
            ]}
          />
        </Box>

        <Box sx={{ minWidth: 220 }}>
          <SelectField
            formik={filterFormik}
            name="ledgerId"
            label="Ledger"
            options={[
              { value: "", label: "All Ledgers" },
              ...ledgers.map((l: any) => ({
                value: String(l.id),
                label: l.ledgerName,
              })),
            ]}
          />
        </Box>

        <Box sx={{ minWidth: 240 }}>
          <SelectField
            formik={filterFormik}
            name="accountId"
            label="Account"
            options={[
              { value: "", label: "All Accounts" },
              ...accounts.map((a: any) => ({
                value: String(a.id),
                label: `${a.accountName}${a.accountNo ? ` (${a.accountNo})` : ""}`,
              })),
            ]}
          />
        </Box>

        {hasFilters && (
          <Box
            component="button"
            onClick={() => setFilters(emptyFilters)}
            sx={{
              border: "1px solid rgba(198, 197, 212, 0.45)",
              background: "#ffffff",
              color: "#64748b",
              borderRadius: "8px",
              padding: "6px 12px",
              fontSize: "0.75rem",
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
              "&:hover": { borderColor: "#b71c1c", color: "#b71c1c" },
            }}
          >
            Clear Filters
          </Box>
        )}
      </Box>

      <DataTable
        columns={columns}
        rows={transactions}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
    </PageWrapper>
  );
}

const tones = {
  income: { bg: "rgba(16, 185, 129, 0.08)", color: "#059669" },
  expense: { bg: "rgba(239, 68, 68, 0.08)", color: "#dc2626" },
};

function SummaryCard({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone: "income" | "expense";
  icon: ReactNode;
}) {
  const scheme = tones[tone];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.75,
        borderRadius: "12px",
        background: "#ffffff",
        border: "1px solid rgba(198, 197, 212, 0.25)",
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: scheme.bg,
          color: scheme.color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Box
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#9ca3af",
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          {label}
        </Box>
        <Box
          sx={{ fontSize: "1.05rem", fontWeight: 800, color: scheme.color }}
        >
          ₹{Number(value || 0).toLocaleString()}
        </Box>
      </Box>
    </Box>
  );
}
